<template>
  <div>
    <!-- Filters bar -->
    <div class="hr-bar">
      <div class="hr-field">
        <label>Company or customer</label>
        <div
          class="hr-input"
          style="display: flex; align-items: center; gap: 7px"
        >
          <b-icon icon="search" font-scale="0.9" style="color: #94a3b8" />
          <input
            v-model="searchQuery"
            placeholder="Search…"
            style="
              border: 0;
              outline: 0;
              font-size: 13px;
              width: 150px;
              background: transparent;
            "
          />
        </div>
      </div>
      <div class="hr-field">
        <label>Membership</label>
        <select class="hr-select" v-model="membershipFilter">
          <option value="all">All</option>
          <option v-for="m in memberships" :key="m" :value="m">{{ m }}</option>
        </select>
      </div>
      <div class="hr-field">
        <label>Profile</label>
        <select class="hr-select" v-model="profileFilter">
          <option value="all">All</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
      </div>
      <div class="hr-field">
        <label>Status</label>
        <div class="hr-seg-toggle">
          <button
            v-for="s in statusOptions"
            :key="s.value"
            :class="{ on: statusFilter === s.value }"
            @click="statusFilter = s.value"
          >
            {{ s.label }}
          </button>
        </div>
      </div>
      <div class="hr-field">
        <label>Company overview</label>
        <select class="hr-select" v-model="selectedCompanyId">
          <option :value="null">Select a company…</option>
          <option v-for="c in companyOptions" :key="c.id" :value="c.id">{{
            c.name
          }}</option>
        </select>
      </div>
      <div class="hr-bar-right">
        <button class="hr-btn hr-btn-primary" @click="exportCsv">
          <b-icon icon="download" font-scale="0.9" /> Export CSV
        </button>
      </div>
    </div>

    <!-- Company overview: total hours logged across all of a company's
         customers, vs. their contracted/budgeted hours - the "Qblox"
         use-case, so an admin can review a company's budget as a whole
         instead of hunting one customer row at a time. -->
    <div class="hr-card" style="margin-bottom: 18px" v-if="companySummary">
      <div style="padding: 16px 18px">
        <div
          style="display: flex; justify-content: space-between; align-items: baseline"
        >
          <div class="hr-section-title" style="padding: 0">
            {{ companySummary.name }}
          </div>
          <div class="hr-hint">
            {{ companySummary.customers.length }} customer{{
              companySummary.customers.length !== 1 ? "s" : ""
            }}
          </div>
        </div>
        <div class="hr-kpis" style="margin-top: 12px">
          <div class="hr-card hr-kpi">
            <div class="k-top">
              <b-icon icon="clock" font-scale="0.9" /> Budgeted hours
            </div>
            <div class="k-val">{{ companySummary.budgetHours }}</div>
          </div>
          <div class="hr-card hr-kpi">
            <div class="k-top">
              <b-icon icon="graph-up" font-scale="0.9" /> Hours logged
            </div>
            <div class="k-val" style="color: #0d9488">
              {{ companySummary.loggedHours }}
            </div>
          </div>
          <div class="hr-card hr-kpi">
            <div class="k-top">
              <b-icon icon="pie-chart" font-scale="0.9" /> Utilization
            </div>
            <div
              class="k-val"
              :style="{
                color: companySummary.overBudget ? '#e11d48' : '#0f172a'
              }"
            >
              {{ companySummary.utilizationPct }}
            </div>
          </div>
        </div>
        <table class="hr-table" style="margin-top: 14px">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Status</th>
              <th style="text-align: right">Hours logged</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in companySummary.customers" :key="c.id">
              <td>{{ c.contact }}</td>
              <td>
                <span v-if="c.isActive" class="hr-badge b-teal">Active</span>
                <span v-else class="hr-badge b-slate">Inactive</span>
              </td>
              <td class="num" style="text-align: right; font-weight: 600">
                {{ c.hoursLogged }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- KPI cards -->
    <div class="hr-kpis">
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="credit-card" font-scale="0.9" /> Subscriptions
        </div>
        <div class="k-val">{{ filtered.length }}</div>
        <div class="k-sub">matching filters</div>
      </div>
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="check-circle" font-scale="0.9" /> Active
        </div>
        <div class="k-val" style="color: #0d9488">{{ activeCount }}</div>
        <div class="k-sub">{{ activePercent }}% of book</div>
      </div>
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="clock" font-scale="0.9" /> Contracted hours
        </div>
        <div class="k-val">{{ contractedHours.toLocaleString() }}</div>
        <div class="k-sub">active subscriptions</div>
      </div>
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="people" font-scale="0.9" /> Avg assistants
        </div>
        <div class="k-val">{{ avgAssistants }}</div>
        <div class="k-sub">per subscription</div>
      </div>
    </div>

    <!-- Subscription hours trend -->
    <div style="width: 100%">
      <div class="hr-card" style="padding: 16px 18px 8px; width: 100%">
        <div
          style="display: flex; justify-content: space-between; align-items: baseline"
        >
          <div class="hr-section-title" style="padding: 0">
            Subscription hours
          </div>
          <div class="hr-hint">
            24-month book · ends
            {{ subTrend[subTrend.length - 1].hours.toLocaleString() }}h
          </div>
        </div>
        <div style="height: 280px; margin-top: 8px">
          <GChart
            type="AreaChart"
            :data="trendChartData"
            :options="trendChartOptions"
          />
        </div>
      </div>
    </div>

    <!-- Membership bar chart + Data table -->
    <div class="hr-grid2" style="margin-top: 20px">
      <!-- Customers by membership -->
      <div class="hr-card" style="padding: 16px 18px 14px">
        <div
          style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px"
        >
          <div class="hr-section-title" style="padding: 0">
            Customers by membership
          </div>
          <div class="hr-legend" style="font-size: 11px">
            <span><i style="background: #0d9488" />Active</span>
            <span><i style="background: #cbd5e1" />Inactive</span>
          </div>
        </div>
        <div
          v-for="row in membershipData"
          :key="row.m"
          class="hr-mem-row"
          style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px"
        >
          <div
            style="font-size: 12.5px; font-weight: 600; color: #475569; min-width: 80px; flex-shrink: 0"
          >
            {{ row.m }}
          </div>
          <div style="flex: 1">
            <div
              class="hr-mem-bar"
              :style="{
                width: `${Math.max(6, (row.active / memMax) * 100)}%`,
                background: '#0d9488'
              }"
            >
              {{ row.active }}
            </div>
            <div
              class="hr-mem-bar"
              :style="{
                width: `${Math.max(6, (row.inactive / memMax) * 100)}%`,
                background: '#cbd5e1',
                color: '#475569',
                marginBottom: 0
              }"
            >
              {{ row.inactive }}
            </div>
          </div>
        </div>
      </div>

      <!-- Data table -->
      <div class="hr-card">
        <div class="hr-scroll">
          <table class="hr-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Profile</th>
                <th>Customer</th>
                <th>Created</th>
                <th>End date</th>
                <th style="text-align: right">Sub. hours</th>
                <th style="text-align: right">Assistants</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filtered.length === 0">
                <td colspan="8">
                  <div class="hr-empty">
                    <div class="e-ic">
                      <b-icon icon="credit-card" font-scale="1.2" />
                    </div>
                    <div
                      style="font-weight: 700; color: #0f172a; margin-bottom: 4px"
                    >
                      No subscriptions match
                    </div>
                    <div style="font-size: 13px">
                      Loosen the filters or clear the search.
                    </div>
                  </div>
                </td>
              </tr>
              <tr v-for="s in filtered" :key="s.id">
                <td style="font-weight: 600">{{ s.company }}</td>
                <td>
                  <span
                    class="hr-badge"
                    :style="{
                      background: profileColors[s.profile] + '1f',
                      color: profileColors[s.profile]
                    }"
                    >{{ s.profile }}</span
                  >
                </td>
                <td style="color: #475569">{{ s.contact }}</td>
                <td class="num" style="color: #64748b">{{ s.created }}</td>
                <td class="num" style="color: #64748b">{{ s.endDate }}</td>
                <td class="num" style="text-align: right; font-weight: 600">
                  {{ s.hours }}
                </td>
                <td class="num" style="text-align: right">
                  {{ s.assistants }}
                </td>
                <td>
                  <span v-if="s.isActive" class="hr-badge b-teal">Active</span>
                  <span v-else class="hr-badge b-slate">Inactive</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="hr-total-foot">
          <span
            >Total · {{ filtered.length }} subscription{{
              filtered.length !== 1 ? "s" : ""
            }}</span
          >
          <span class="num">{{ sumHours.toLocaleString() }} h</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getReportSubscriptions } from "@/apis/customers";
import _ from "lodash";

const MEMBERSHIPS = ["Corporate", "Business", "Unlimited", "Enterprise"];

const SUB_TREND = [
  1490,
  1465,
  1455,
  1415,
  1440,
  1440,
  1425,
  1420,
  1405,
  1380,
  1355,
  1360,
  1295,
  1270,
  1210,
  1180,
  1255,
  1255,
  1255,
  1255,
  1255,
  1255,
  1255,
  1230
].map((hours, i) => ({
  label: `${
    [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ][i % 12]
  } ${i < 12 ? "25" : "26"}`,
  hours
}));

export default {
  name: "SubscriptionsView",
  data() {
    return {
      searchQuery: "",
      membershipFilter: "all",
      profileFilter: "all",
      statusFilter: "active",
      selectedCompanyId: null,
      memberships: MEMBERSHIPS,
      profileColors: {
        A: "#7fb2d4",
        B: "#1e3a5f",
        C: "#e11d48"
      },
      subs: [],
      subTrend: SUB_TREND,
      statusOptions: [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" }
      ],
      trendChartOptions: {
        backgroundColor: "transparent",
        height: 280,
        chartArea: { left: 40, right: 8, top: 8, bottom: 24 },
        legend: "none",
        colors: ["#0d9488"],
        areaOpacity: 0.18,
        lineWidth: 2.5,
        hAxis: {
          textStyle: { fontSize: 10, color: "#94a3b8" },
          gridlines: { count: 0 }
        },
        vAxis: {
          textStyle: { fontSize: 10, color: "#94a3b8" },
          minValue: 1050,
          maxValue: 1550,
          gridlines: { color: "#eef2f6" }
        },
        curveType: "function"
      },
      isLoading: false
    };
  },
  computed: {
    // Selecting a company scopes the WHOLE page (KPIs, membership chart,
    // table); the search box then narrows to a customer within that scope.
    filtered() {
      const q = this.searchQuery.trim().toLowerCase();
      let rows = this.selectedCompanyId
        ? this.subs.filter(s => s.companyId === this.selectedCompanyId)
        : this.subs;
      if (q) {
        rows = rows.filter(
          s =>
            (s.company || "").toLowerCase().includes(q) ||
            (s.contact || "").toLowerCase().includes(q)
        );
      }
      return [...rows].sort(
        (a, b) =>
          (a.company || "").localeCompare(b.company || "") ||
          (a.contact || "").localeCompare(b.contact || "")
      );
    },
    sumHours() {
      return this.filtered.reduce((a, s) => a + s.hours, 0);
    },
    activeCount() {
      return this.filtered.filter(s => s.isActive).length;
    },
    activePercent() {
      return this.filtered.length
        ? Math.round((this.activeCount / this.filtered.length) * 100)
        : 0;
    },
    // `hours` is a company-wide budget repeated on every customer row, so
    // dedupe by company before summing or a multi-customer company counts Nx.
    contractedHours() {
      const seen = new Set();
      return this.filtered.reduce((total, s) => {
        const key = s.companyId || s.id;
        if (!s.isActive || seen.has(key)) return total;
        seen.add(key);
        return total + s.hours;
      }, 0);
    },
    avgAssistants() {
      if (!this.filtered.length) return "0";
      const total = this.filtered.reduce((a, s) => a + s.assistants, 0);
      return (total / this.filtered.length).toFixed(1);
    },
    membershipData() {
      const groups = {};
      this.filtered.forEach(s => {
        const m = s.membership && s.membership !== "N/A" ? s.membership : null;
        if (!m) return;
        if (!groups[m]) groups[m] = { m, active: 0, inactive: 0 };
        groups[m][s.isActive ? "active" : "inactive"]++;
      });
      const rows = Object.values(groups);
      return rows.length
        ? rows
        : MEMBERSHIPS.map(m => ({ m, active: 0, inactive: 0 }));
    },
    memMax() {
      return Math.max(
        1,
        ...this.membershipData.flatMap(r => [r.active, r.inactive])
      );
    },
    trendChartData() {
      const data = [["Month", "Hours"]];
      this.subTrend.forEach(t => data.push([t.label, t.hours]));
      return data;
    },
    // Unique companies (excludes standalone "Individual" customers, who
    // have no companyId to group under), sorted alphabetically.
    companyOptions() {
      const seen = new Map();
      this.subs.forEach(s => {
        if (s.companyId && !seen.has(s.companyId)) {
          seen.set(s.companyId, { id: s.companyId, name: s.company });
        }
      });
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    // All of the selected company's customers + budget vs. actual-logged
    // hours, summed across every customer under that company.
    companySummary() {
      if (!this.selectedCompanyId) return null;
      const all = this.subs.filter(s => s.companyId === this.selectedCompanyId);
      if (!all.length) return null;
      // Typing a customer name narrows the panel to that customer; a
      // company-name match (or empty search) keeps the whole company.
      const q = this.searchQuery.trim().toLowerCase();
      const customers = all
        .filter(
          s =>
            !q ||
            (s.contact || "").toLowerCase().includes(q) ||
            (s.company || "").toLowerCase().includes(q)
        )
        .sort((a, b) => a.contact.localeCompare(b.contact));
      // `hours` (the budget) is the same figure repeated on every row of
      // the company - it's the company's total, not per-customer.
      const budgetHours = all[0].hours;
      const loggedHours = customers.reduce(
        (sum, c) => sum + (c.hoursLogged || 0),
        0
      );
      return {
        name: all[0].company,
        customers,
        budgetHours,
        loggedHours: Math.round(loggedHours * 10) / 10,
        overBudget: budgetHours > 0 && loggedHours > budgetHours,
        utilizationPct: budgetHours
          ? `${Math.round((loggedHours / budgetHours) * 100)}%`
          : "—"
      };
    }
  },
  watch: {
    searchQuery: {
      handler: "debouncedLoad"
    },
    membershipFilter: "loadSubscriptions",
    profileFilter: "loadSubscriptions",
    statusFilter: "loadSubscriptions"
  },
  created() {
    this.debouncedLoad = _.debounce(this.loadSubscriptions, 300);
  },
  mounted() {
    this.loadSubscriptions();
  },
  methods: {
    async loadSubscriptions() {
      this.isLoading = true;
      try {
        const params = {
          search: this.searchQuery,
          membership: this.membershipFilter,
          profile: this.profileFilter,
          status: this.statusFilter
        };
        const res = await getReportSubscriptions(params);
        this.subs = res.data.results || [];
      } catch (err) {
        console.error("Error loading report subscriptions:", err);
      } finally {
        this.isLoading = false;
      }
    },
    exportCsv() {
      const head = [
        "Company",
        "Profile",
        "Customer",
        "Created",
        "End date",
        "Subscription hours",
        "Assigned assistants",
        "Active"
      ];
      const rows = this.subs.map(s => [
        s.company,
        s.profile,
        s.contact,
        s.created,
        s.endDate,
        s.hours,
        s.assistants,
        s.isActive
      ]);
      const csv = [head, ...rows]
        .map(r =>
          r
            .map(c => {
              const v = String(c ?? "");
              return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
            })
            .join(",")
        )
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "headroom_subscriptions.csv";
      a.click();
      URL.revokeObjectURL(url);
      this.$emit("notify", "CSV downloaded");
    }
  }
};
</script>
